import { Card, Text } from '@tremor/react';
import type { Entitlement } from '../api/admin';
import { t } from '../lib/i18n';

interface Props {
  entitlement: Entitlement | null;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  // The Go side serializes time.Time as RFC3339. Render YYYY-MM-DD only.
  return iso.slice(0, 10);
}

/**
 * Top-of-page banner that shows seat usage and license expiry. Color escalates:
 * green when under 80%, amber at 80%+, red at the seat limit or once the
 * license drops out of "ok". The license source (`trial` vs `remote`) gets a
 * small badge so the owner can tell at a glance which they're on.
 */
export default function SeatBanner({ entitlement }: Props) {
  if (!entitlement) return null;

  const { seats_allowed, seats_used, expires_at, source, status } = entitlement;
  const atLimit = seats_allowed > 0 && seats_used >= seats_allowed;
  const overThreshold = seats_allowed > 0 && seats_used / seats_allowed >= 0.8;
  let tone: 'green' | 'amber' | 'red' = 'green';
  if (status === 'expired' || atLimit) tone = 'red';
  else if (status === 'grace' || overThreshold) tone = 'amber';
  const toneClass = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
    red:   'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
  }[tone];

  return (
    <Card decoration="left" className={`${toneClass}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Text className="font-semibold">
          {t.admin.seatsUsed.replace('{used}', String(seats_used)).replace('{allowed}', String(seats_allowed))}
        </Text>
        <Text>· {t.admin.seatsExpires.replace('{date}', fmtDate(expires_at))}</Text>
        {source === 'trial' && (
          <span className="inline-flex items-center rounded-full bg-tremor-background px-2 py-0.5 text-xs font-medium border border-tremor-border">
            {t.admin.trialBadge}
          </span>
        )}
        {status === 'grace'   && <Text className="text-amber-800 dark:text-amber-300">· {t.admin.licenseGrace}</Text>}
        {status === 'expired' && <Text className="text-red-800 dark:text-red-300">· {t.admin.licenseExpired}</Text>}
      </div>
    </Card>
  );
}
